import { getAuthToken } from "../../../auth/authClient";

const API_BASE = (process.env.REACT_APP_MORFEUSZ_4E_API_URL || "").replace(/\/$/, "");

const followupUrl = (id = "") => `${API_BASE}/followup-e2e/${encodeURIComponent(id)}`;

const requestHeaders = (options = {}) => {
  const token = getAuthToken();
  return {
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
};

const makeError = (data, status) => {
  const error = new Error(data?.message || `followup-e2e-http-${status}`);
  error.code = data?.code || `followup-e2e-http-${status}`;
  error.status = status || null;
  return error;
};

const parseResponse = async (response) => {
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) throw makeError(data, response.status);
  return data;
};

const normalizePack = (followup = {}) => {
  const e2e = followup.e2e && typeof followup.e2e === "object" ? followup.e2e : {};
  const iv = String(e2e.iv || "");
  const ct = String(e2e.ct || "");
  const aad = String(e2e.aad || "");
  if (!iv || !ct) return null;
  return { iv, ct, aad };
};

const ensureVaultUnlocked = async () => {
  if (typeof window === "undefined" || !window.MDVaultSession || !window.morfeuszE2E) {
    throw makeError({ code: "vault_not_ready", message: "Sejf E2E nie jest gotowy." }, 400);
  }

  const unlocked = await window.MDVaultSession.ensureUnlocked();
  if (!unlocked?.ok && !unlocked?.state?.unlocked) {
    throw makeError({ code: "vault_locked", message: "Odblokuj sejf, aby zapisać odpowiedź follow-up." }, 423);
  }
};

export const encryptFollowupE2E = async (followup = {}) => {
  await ensureVaultUnlocked();

  const plain = JSON.stringify({
    source: followup.source || "backend_4e",
    q: String(followup.q || ""),
    a: String(followup.a || followup.answer || ""),
    used: Boolean(followup.used || followup.q || followup.a || followup.answer),
    createdAt: followup.createdAt || new Date().toISOString(),
    profileUsed: Boolean(followup.profileUsed),
  });

  const e2e = await window.morfeuszE2E.encryptText(plain);
  return {
    source: followup.source || "backend_4e",
    used: true,
    createdAt: followup.createdAt || new Date().toISOString(),
    profileUsed: Boolean(followup.profileUsed),
    e2e,
  };
};

export const decryptFollowupE2E = async (followup = {}) => {
  if (!followup || typeof followup !== "object") return null;
  if (followup.q && followup.a) return followup;

  const e2e = normalizePack(followup);
  if (!e2e) return followup;
  await ensureVaultUnlocked();

  const plain = await window.morfeuszE2E.decryptText(e2e.iv, e2e.ct, e2e.aad || "");
  const parsed = JSON.parse(plain || "{}");

  return {
    ...followup,
    source: parsed.source || followup.source || "backend_4e",
    q: String(parsed.q || ""),
    a: String(parsed.a || parsed.answer || ""),
    used: Boolean(parsed.used || parsed.q || parsed.a || parsed.answer || followup.used),
    createdAt: parsed.createdAt || followup.createdAt || "",
    profileUsed: Boolean(parsed.profileUsed || followup.profileUsed),
    e2e,
  };
};

const request = async (id, options = {}) => {
  if (!API_BASE) throw makeError({ code: "followup-e2e-api-missing", message: "Brak konfiguracji API follow-up E2E." }, 400);

  const response = await fetch(followupUrl(id), {
    credentials: "include",
    ...options,
    headers: requestHeaders(options),
  });
  return parseResponse(response);
};

export const followupE2EClient = {
  isConfigured: () => Boolean(API_BASE),
  ensureReady: async (id) => {
    try {
      await request(id);
      return true;
    } catch (error) {
      const readyError = makeError({
        code: "followup_e2e_storage_missing",
        message: "Moduł bezpiecznego zapisu follow-up E2E nie jest aktywny.",
      }, error?.status || 503);
      readyError.cause = error;
      throw readyError;
    }
  },
  get: async (id) => {
    const data = await request(id);
    return {
      ...data,
      followup: data.followup ? await decryptFollowupE2E(data.followup) : null,
    };
  },
  save: async (id, followup) => {
    const encrypted = await encryptFollowupE2E(followup);
    const data = await request(id, { method: "PATCH", body: JSON.stringify({ followup: encrypted }) });
    return {
      ...data,
      followup: {
        ...(data.followup || encrypted),
        q: followup.q || "",
        a: followup.a || followup.answer || "",
        used: true,
      },
    };
  },
  remove: (id) => request(id, { method: "DELETE" }),
};
